<script setup>
import { computed, ref } from 'vue'
import Icon from './ui/Icon.vue'
import SectionTitle from './ui/SectionTitle.vue'
import { isHidden } from '../stores/sections'
import { alerts, budgetRows, focusBudget, planHealth, setFilter, showMonth, viewedMonth } from '../stores'
import { t } from '../stores/i18n'
import { state } from '../stores/state'

const expanded = ref(false)
const shown = computed(() => (expanded.value ? alerts.value : alerts.value.slice(0, 3)))
const hidden = computed(() => Math.max(0, alerts.value.length - shown.value.length))

const TONES = {
  danger: { ring: 'border-danger/35 bg-danger-soft', text: 'text-danger', icon: 'alert' },
  warn: { ring: 'border-warn/35 bg-warn-soft', text: 'text-warn', icon: 'alert' },
  good: { ring: 'border-accent/35 bg-accent-soft', text: 'text-accent', icon: 'check' },
  info: { ring: 'border-line bg-raised', text: 'text-muted', icon: 'info' },
}

function focus(alert) {
  // A goal is not something a transaction is filed under, so its alert opens
  // the goals themselves rather than filtering the ledger by it.
  if (alert.goalId) {
    state.ui.panel = 'goals'
    return
  }
  if (alert.budgetId) {
    const row = budgetRows.value.find((r) => r.budgetId === alert.budgetId)
    if (!row) return
    focusBudget(row)
  } else if (alert.category) setFilter('categories', [alert.category])
  else return
  state.ui.panel = 'transactions'
  // An alert is about the month on screen, so the window narrows to it.
  showMonth(viewedMonth.value)
}
</script>

<template>
  <section v-if="alerts.length" class="min-w-0" :class="isHidden('alerts') ? '' : 'w-full'" data-section="alerts">
    <SectionTitle id="alerts" :title="t('alerts.title')">
      <span class="tnum text-faint">
        {{ t('alerts.seen', { matched: planHealth.matched, total: planHealth.total }) }}
      </span>
    </SectionTitle>
    <ul v-if="!isHidden('alerts')" class="grid gap-2">
      <li v-for="a in shown" :key="a.id">
        <button
          class="flex w-full items-start gap-3 rounded-2xl border px-3.5 py-3 text-left transition
                 hover:brightness-[1.08]"
          :class="TONES[a.tone].ring"
          @click="focus(a)"
        >
          <Icon :name="TONES[a.tone].icon" :size="17" class="mt-0.5" :class="TONES[a.tone].text" />
          <span class="min-w-0 flex-1">
            <span class="block text-[14px] leading-snug font-semibold">{{ a.title }}</span>
            <span class="mt-0.5 block text-[12.5px] leading-snug text-muted">{{ a.detail }}</span>
          </span>
        </button>
      </li>
    </ul>
    <button v-if="!isHidden('alerts') && (hidden || expanded)" class="btn-ghost mt-1.5 !px-1 text-[13px]" @click="expanded = !expanded">
      {{ expanded ? t('alerts.less') : t('alerts.more', { count: hidden }) }}
    </button>
  </section>

  <!-- Nothing off plan is still this section: the same title, folded the same way. -->
  <section v-else-if="planHealth.total" class="min-w-0" :class="isHidden('alerts') ? '' : 'w-full'" data-section="alerts">
    <SectionTitle id="alerts" :title="t('alerts.title')" />
    <div v-if="!isHidden('alerts')" class="flex items-center gap-3 rounded-2xl border border-accent/30 bg-accent-soft px-3.5 py-3">
      <Icon name="check" :size="17" class="text-accent" />
      <p class="text-[14px] font-medium">
        {{ t('alerts.allWell') }}
        <span class="text-muted">
          — {{ t('alerts.allWellDetail', { matched: planHealth.matched, total: planHealth.total }) }}
        </span>
      </p>
    </div>
  </section>
</template>
